import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Shield, ShieldCheck, ShieldOff, Copy, Check } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { totpVerifySchema, type TotpVerifyInput } from '@dcp/shared'
import { useMFA } from '@/hooks/useMFA'
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Separator,
} from '@atrvd/saas-ui'

/** Data received from a successful TOTP enrollment call */
interface EnrollmentData {
  /** Factor ID assigned by Supabase, used for verification and unenroll */
  id: string
  /** Data URI of the QR code image to scan with an authenticator app */
  qr_code: string
  /** TOTP secret for users who prefer manual entry */
  secret: string
}

/** Controls which view the MFASection renders */
type MFAStep = 'idle' | 'enrolling' | 'enrolled'

/**
 * MFA enrollment and management section for the profile settings page.
 *
 * Renders one of three states:
 * - idle: no MFA enrolled — prompts the user to enable 2FA
 * - enrolling: QR code + manual secret + TOTP code verification form
 * - enrolled: status banner + option to disable 2FA with confirmation step
 *
 * All Supabase MFA operations are delegated to the useMFA hook.
 */
export function MFASection() {
  const { factors, enroll, verifyAndActivate, unenroll, hasMFA } = useMFA()

  /**
   * Current UI step. Synced with hasMFA via useEffect on initial load only.
   * After the first sync, user actions (enroll/verify/unenroll) control the step.
   */
  const [step, setStep] = useState<MFAStep>('idle')

  /** Tracks whether the initial factors→step sync has occurred */
  const [initialSyncDone, setInitialSyncDone] = useState(false)

  /** Data returned by the enroll() call, displayed during the enrolling step */
  const [enrollmentData, setEnrollmentData] = useState<EnrollmentData | null>(null)

  /** Whether the unenroll confirmation prompt is visible */
  const [confirmingDisable, setConfirmingDisable] = useState(false)

  /**
   * Sync step with hasMFA once when the factors query first resolves.
   * After the initial sync, user actions (enroll/verify/unenroll) control the step
   * — subsequent refetches won't interrupt an active enrollment or override
   * a step that was just set by a user action.
   */
  useEffect(() => {
    if (factors.isSuccess && !initialSyncDone) {
      setStep(hasMFA ? 'enrolled' : 'idle')
      setInitialSyncDone(true)
    }
  }, [factors.isSuccess, hasMFA, initialSyncDone])

  /** Transient flag that shows a checkmark for 2 seconds after copying the secret */
  const [secretCopied, setSecretCopied] = useState(false)

  /** Tracks whether an unenroll request is in flight */
  const [isUnenrolling, setIsUnenrolling] = useState(false)

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isSubmitting },
  } = useForm<TotpVerifyInput>({
    resolver: zodResolver(totpVerifySchema),
  })

  /**
   * Starts TOTP enrollment by calling enroll(), then switches the UI to the
   * enrolling step so the QR code and verification form are shown.
   */
  const handleStartEnrollment = async () => {
    try {
      const data = await enroll()
      setEnrollmentData(data)
      setStep('enrolling')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start enrollment'
      toast.error(message)
    }
  }

  /**
   * Cancels an in-progress enrollment and returns to the idle step.
   * Resets the form and clears any stored enrollment data.
   */
  const handleCancelEnrollment = () => {
    setEnrollmentData(null)
    resetForm()
    setStep('idle')
  }

  /**
   * Verifies the TOTP code entered by the user and activates the factor.
   * On success, transitions to the enrolled step and shows a success toast.
   *
   * @param data - Validated form values containing the 6-digit TOTP code
   */
  const handleVerify = async (data: TotpVerifyInput) => {
    if (!enrollmentData) return

    try {
      await verifyAndActivate(enrollmentData.id, data.code)
      toast.success('Two-factor authentication enabled')
      setEnrollmentData(null)
      resetForm()
      setStep('enrolled')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to verify code'
      toast.error(message)
    }
  }

  /**
   * Copies the TOTP secret to the clipboard and shows a transient checkmark
   * for 2 seconds to confirm the copy action.
   */
  const handleCopySecret = async () => {
    if (!enrollmentData) return
    try {
      await navigator.clipboard.writeText(enrollmentData.secret)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    } catch {
      toast.error('Failed to copy to clipboard. Please copy manually.')
    }
  }

  /**
   * Unenrolls the first verified TOTP factor found in the factors list.
   * On success, transitions to idle and hides the confirmation prompt.
   */
  const handleUnenroll = async () => {
    const verifiedFactor = factors.data?.find((f) => f.status === 'verified')
    if (!verifiedFactor) return

    setIsUnenrolling(true)
    try {
      await unenroll(verifiedFactor.id)
      toast.success('Two-factor authentication disabled')
      setStep('idle')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to disable 2FA'
      toast.error(message)
    } finally {
      setConfirmingDisable(false)
      setIsUnenrolling(false)
    }
  }

  // Show a loading spinner while the initial factors query is in flight
  if (factors.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  // ── Enrolling step: QR code + manual secret + verification form ──────────
  if (step === 'enrolling' && enrollmentData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Set Up Two-Factor Authentication
          </CardTitle>
          <CardDescription>Scan the QR code with your authenticator app</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4">
            {/* QR code for authenticator apps */}
            <img
              src={enrollmentData.qr_code}
              alt="QR Code"
              className="w-48 h-48"
            />

            <Separator />

            {/* Manual entry fallback */}
            <div className="w-full space-y-2">
              <p className="text-sm text-muted-foreground">Can&apos;t scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono bg-muted px-3 py-2 rounded flex-1 break-all">
                  {enrollmentData.secret}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCopySecret}
                  aria-label="Copy secret to clipboard"
                >
                  {secretCopied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <Separator />

            {/* TOTP verification form */}
            <form onSubmit={handleSubmit(handleVerify)} className="w-full">
              <div className="space-y-2">
                <Label htmlFor="mfa-code">Verification Code</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="000000"
                  {...register('code')}
                />
                {errors.code && (
                  <p className="text-sm text-destructive">{errors.code.message}</p>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelEnrollment}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    'Verify & Enable'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    )
  }

  // ── Enrolled step: status banner + disable option ────────────────────────
  if (step === 'enrolled') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-600" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>Your account is protected with 2FA</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Active 2FA status banner */}
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <ShieldCheck className="h-4 w-4 text-green-600" />
            <AlertDescription>
              Two-factor authentication is enabled. Your account has an extra layer of security.
            </AlertDescription>
          </Alert>

          <Separator />

          <div>
            <p className="text-sm font-medium">Disable Two-Factor Authentication</p>
            <p className="text-sm text-muted-foreground mb-3">
              This will remove the extra security from your account
            </p>

            {confirmingDisable ? (
              /* Confirmation prompt shown after the user clicks "Disable 2FA" */
              <Alert variant="destructive">
                <AlertDescription>
                  Are you sure? This will disable two-factor authentication.
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmingDisable(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleUnenroll}
                      disabled={isUnenrolling}
                    >
                      {isUnenrolling ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          Disabling…
                        </>
                      ) : (
                        'Yes, Disable 2FA'
                      )}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="text-destructive"
                onClick={() => setConfirmingDisable(true)}
              >
                <ShieldOff className="mr-2 h-4 w-4" />
                Disable 2FA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  // ── Idle step: 2FA not enabled, prompt to enroll ─────────────────────────
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>Add an extra layer of security to your account</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            Two-factor authentication is not enabled. We recommend enabling 2FA for better account
            security.
          </AlertDescription>
        </Alert>

        <Button type="button" onClick={handleStartEnrollment}>
          Enable Two-Factor Authentication
        </Button>
      </CardContent>
    </Card>
  )
}
